import { useEffect, useState } from 'react';
import type { PartnerPayoutListItem, PartnerPayoutPaymentMethod } from '@accestra/types';
import {
  Button,
  C,
  Card,
  Field,
  Pill,
  TableWrap,
  asNullableText,
  friendlyError,
  formatMaybeDateTime,
  formatMoney,
  inputStyle,
  paFetch,
  statusColor,
  tdS,
  textareaStyle,
  thS,
  titleCase,
  toDateTimeLocalInput,
  toNullableIsoString,
} from './shared';

const PAYOUT_STATUS_OPTIONS = ['all', 'draft', 'approved', 'paid', 'cancelled'] as const;
const PAYMENT_METHOD_OPTIONS: Array<PartnerPayoutPaymentMethod | 'none'> = [
  'none',
  'bank_transfer',
  'wise',
  'paypal',
  'other',
];

interface PayoutListResponse {
  payouts: PartnerPayoutListItem[];
  total: number;
}

interface PayoutDetailResponse {
  payout: PartnerPayoutListItem;
}

function emptyPaidForm() {
  return {
    paymentMethod: 'none' as PartnerPayoutPaymentMethod | 'none',
    paymentReference: '',
    paidAt: '',
    notes: '',
  };
}

export default function PayoutsPanel() {
  const [statusFilter, setStatusFilter] = useState<(typeof PAYOUT_STATUS_OPTIONS)[number]>('all');
  const [payouts, setPayouts] = useState<PartnerPayoutListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPayout, setSelectedPayout] = useState<PartnerPayoutListItem | null>(null);
  const [paidForm, setPaidForm] = useState(emptyPaidForm);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  async function loadPayouts() {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        page: '1',
        pageSize: '100',
      });
      if (statusFilter !== 'all') {
        query.set('status', statusFilter);
      }

      const result = await paFetch<PayoutListResponse>('GET', `/platform-admin/partner-payouts?${query.toString()}`);
      setPayouts(result.payouts);
      if (!result.payouts.find((payout) => payout.id === selectedId)) {
        setSelectedId(result.payouts[0]?.id ?? null);
      }
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setLoading(false);
    }
  }

  async function loadPayoutDetail(payoutId: string) {
    try {
      const result = await paFetch<PayoutDetailResponse>('GET', `/platform-admin/partner-payouts/${payoutId}`);
      setSelectedPayout(result.payout);
    } catch (nextError) {
      setError(friendlyError(nextError));
    }
  }

  useEffect(() => {
    void loadPayouts();
  }, [statusFilter]);

  useEffect(() => {
    if (selectedId) {
      void loadPayoutDetail(selectedId);
    } else {
      setSelectedPayout(null);
    }
  }, [selectedId]);

  useEffect(() => {
    if (!selectedPayout) {
      return;
    }

    setPaidForm({
      paymentMethod: selectedPayout.paymentMethod ?? 'none',
      paymentReference: selectedPayout.paymentReference ?? '',
      paidAt: toDateTimeLocalInput(selectedPayout.paidAt),
      notes: selectedPayout.notes ?? '',
    });
  }, [selectedPayout]);

  async function approvePayout() {
    if (!selectedPayout) {
      return;
    }

    setBusyAction('approve');
    setFeedback(null);
    setError(null);
    try {
      await paFetch('POST', `/platform-admin/partner-payouts/${selectedPayout.id}/approve`);
      await loadPayouts();
      await loadPayoutDetail(selectedPayout.id);
      setFeedback('Payout approved.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  async function markPaid() {
    if (!selectedPayout) {
      return;
    }

    setBusyAction('mark-paid');
    setFeedback(null);
    setError(null);
    try {
      await paFetch('POST', `/platform-admin/partner-payouts/${selectedPayout.id}/mark-paid`, {
        paymentMethod: paidForm.paymentMethod === 'none' ? null : paidForm.paymentMethod,
        paymentReference: asNullableText(paidForm.paymentReference),
        paidAt: toNullableIsoString(paidForm.paidAt),
        notes: asNullableText(paidForm.notes),
      });
      await loadPayouts();
      await loadPayoutDetail(selectedPayout.id);
      setFeedback('Payout marked paid.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(360px, 0.95fr)', gap: 18 }}>
      <Card
        title="Payouts"
        subtitle="Review payout drafts, approve them, and record manual payment references when funds are sent."
        action={(
          <div style={{ display: 'flex', gap: 8 }}>
            <select style={inputStyle} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as (typeof PAYOUT_STATUS_OPTIONS)[number])}>
              {PAYOUT_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All statuses' : titleCase(status)}
                </option>
              ))}
            </select>
            <Button onClick={() => void loadPayouts()}>Refresh</Button>
          </div>
        )}
      >
        {loading ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Loading payouts…</div>
        ) : (
          <TableWrap>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Partner</th>
                  <th style={thS}>Amount</th>
                  <th style={thS}>Status</th>
                  <th style={thS}>Created</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...tdS, textAlign: 'center', color: C.muted }}>No payouts found.</td>
                  </tr>
                ) : payouts.map((payout) => (
                  <tr
                    key={payout.id}
                    onClick={() => setSelectedId(payout.id)}
                    style={{
                      cursor: 'pointer',
                      background: selectedId === payout.id ? 'rgba(14, 165, 164, 0.06)' : 'transparent',
                    }}
                  >
                    <td style={tdS}>
                      <div style={{ fontWeight: 760 }}>{payout.partnerName}</div>
                      <div style={{ color: C.muted, fontSize: 12 }}>{payout.commissionCount} commission rows</div>
                    </td>
                    <td style={tdS}>{formatMoney(payout.totalAmount, payout.currency)}</td>
                    <td style={tdS}><Pill label={titleCase(payout.status)} tone={statusColor(payout.status)} /></td>
                    <td style={tdS}>{formatMaybeDateTime(payout.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card
        title="Payout detail"
        subtitle="Approve the draft and record when the payout was sent, how it was paid, and the manual reference."
      >
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', color: C.danger, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}
        {feedback && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 12px', color: C.success, fontSize: 13, marginBottom: 12 }}>
            {feedback}
          </div>
        )}

        {!selectedPayout ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Select a payout to review it.</div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 780, color: C.text }}>{selectedPayout.partnerName}</div>
                <div style={{ color: C.muted, fontSize: 13 }}>{formatMoney(selectedPayout.totalAmount, selectedPayout.currency)}</div>
              </div>
              <Pill label={titleCase(selectedPayout.status)} tone={statusColor(selectedPayout.status)} />
            </div>

            <div style={{ color: C.muted, fontSize: 12 }}>
              Created {formatMaybeDateTime(selectedPayout.createdAt)} · {selectedPayout.commissionCount} commission rows
            </div>

            <Field label="Payment method">
              <select style={inputStyle} value={paidForm.paymentMethod} onChange={(event) => setPaidForm((current) => ({ ...current, paymentMethod: event.target.value as PartnerPayoutPaymentMethod | 'none' }))}>
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === 'none' ? 'Not set' : titleCase(option)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Payment reference">
              <input style={inputStyle} value={paidForm.paymentReference} onChange={(event) => setPaidForm((current) => ({ ...current, paymentReference: event.target.value }))} />
            </Field>
            <Field label="Paid at">
              <input type="datetime-local" style={inputStyle} value={paidForm.paidAt} onChange={(event) => setPaidForm((current) => ({ ...current, paidAt: event.target.value }))} />
            </Field>
            <Field label="Notes">
              <textarea style={textareaStyle} value={paidForm.notes} onChange={(event) => setPaidForm((current) => ({ ...current, notes: event.target.value }))} />
            </Field>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selectedPayout.status !== 'approved' && selectedPayout.status !== 'paid' && (
                <Button variant="primary" disabled={busyAction !== null} onClick={() => void approvePayout()}>
                  {busyAction === 'approve' ? 'Approving…' : 'Approve payout'}
                </Button>
              )}
              {selectedPayout.status !== 'paid' && selectedPayout.status !== 'cancelled' && (
                <Button disabled={busyAction !== null} onClick={() => void markPaid()}>
                  {busyAction === 'mark-paid' ? 'Saving…' : 'Mark paid'}
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
